import {action, computed, makeObservable, observable} from 'mobx';
import {first, intersection, isEmpty, map, range, size, sortBy} from 'lodash';

import {stringFromSpeed} from '../utils';
import {PORT_ROLES} from '../const';
import {PortRoleType, SpeedType} from '../types';

class Port {
  @observable roles: PortRoleType[] = [];
  @observable speed: SpeedType = null;
  @observable isTaken = false;

  @observable.ref origin: Port | null = null;

  @computed
  get rolesCount(): number {
    return size(this.roles);
  }

  @computed
  get rolesList(): string {
    return sortBy(this.roles).join(', ');
  }

  @computed
  get asString(): string {
    return `[${this.isTaken ? '✘' : '✓'}${this.speedString}:${this.rolesList}]`;
  }

  @computed
  get summary(): string {
    return `${this.speedString}:${this.rolesList}`;
  }

  @computed
  get role(): string {
    return first(this.roles) || '';
  }

  @computed
  get speedString(): string {
    return stringFromSpeed(this.speed);
  }

  @computed
  get hasSpineRole(): boolean {
    return this.matchesRole(PORT_ROLES.SPINE);
  }

  @action
  release(): void {
    this.isTaken = false;
  }

  clone(noLinkToOrigin?: boolean): Port {
    const clone = new Port(this.speed, this.roles);
    if (!noLinkToOrigin) clone.origin = this;
    return clone;
  }

  @action
  init(speed: SpeedType, roles: PortRoleType[]) {
    this.speed = speed;
    this.roles = roles;
  }

  correspondsTo({speedString, roles}, freeOnly?: boolean): boolean {
    return this.speedString === speedString && this.matchesRole(roles[0], freeOnly);
  }

  matchesRole(role: PortRoleType, freeOnly?: boolean) {
    return (!freeOnly || !this.isTaken) && this.roles.includes(role);
  }

  matchesAnyOfRoles(roles) {
    if (isEmpty(roles)) return true;
    return size(intersection(roles, this.roles)) > 0;
  }

  constructor(speed: SpeedType, roles: PortRoleType[]) {
    this.init(speed, roles);
    makeObservable(this);
  }

  static parseGroup({speed, roles, count}): Port[] {
    return map(range(count), () => new Port(speed, roles));
  }

  static generate(count: number, speed: SpeedType, role: PortRoleType) {
    if (!count) return [];
    return Port.parseGroup({speed, count, roles: [role]});
  }
}

export default Port;
